import { useState } from 'react'

import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ExportYearlyBalanceForm from 'modules/shift_management/lib/forms/ExportYearlyBalanceForm'

import FormContent from './FormContent'
import useExportYearlyBalance from './useExportYearlyBalance'

interface Props {
  onClose: () => void
  from: string
}

const ExportYearlyBalanceModal = ({ onClose, from }: Props) => {
  const [teamsIdsSelection, setTeamsIdsSelection] = useLocalStorage<string[]>(
    LOCAL_STORAGE_KEYS.shiftManagementInsightsTeamSelection,
    []
  )

  const [locationIdsSelection, setLocationsIdsSelection] = useLocalStorage<string[]>(
    LOCAL_STORAGE_KEYS.shiftManagementInsightsLocationSelection,
    []
  )

  const [teamsIds, setTeamsIds] = useState<string[]>(teamsIdsSelection ?? [])
  const [locationsIds, setLocationsIds] = useState<string[]>(locationIdsSelection ?? [])
  const form = useLazyRef<ExportYearlyBalanceForm>(() => new ExportYearlyBalanceForm()).current
  const startDate = form.get('start_date').value
  const endDate = form.get('end_date').value
  const hasSelectedWeek = !!startDate && !!endDate
  const hasSelectedTeamOrLocation = !!(teamsIds.length || locationsIds.length)
  const isFormComplete = hasSelectedWeek && hasSelectedTeamOrLocation

  const { onSubmit, submitting } = useExportYearlyBalance({
    startDate,
    endDate,
    teamsIds,
    locationsIds,
    from,
    onSuccess: () => onClose(),
  })

  const description = () => {
    return (
      <Box gap="s8">
        <Text.Normal>
          {i18n.t('shift_management.export_annual_balance.modal.description_2')}
        </Text.Normal>
        <Box maxWidth="fit-content">
          <Link.Href
            href={helpCenterUrl('how_to_view_and_export_the_annual_balance_report')}
            target="_blank"
          >
            {i18n.t('shift_management.export_annual_balance.modal.learn_more')}
          </Link.Href>
        </Box>
      </Box>
    )
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('shift_management.export_annual_balance.modal.title')}
      description={description()}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={submitting || !isFormComplete} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <FormContent
        form={form}
        selectedTeamIds={teamsIds}
        selectedLocationIds={locationsIds}
        setLocationsIdsSelection={setLocationsIdsSelection}
        setLocationsIds={setLocationsIds}
        setTeamsIds={setTeamsIds}
        setTeamsIdsSelection={setTeamsIdsSelection}
        hasSelectedTeamOrLocation={hasSelectedTeamOrLocation}
      />
    </Modal>
  )
}

export default observer(ExportYearlyBalanceModal)
